import React, { useEffect, useState } from "react";
import "./styles.css";
import { toast } from "react-toastify";
import { PlanoLoaderModal } from "../../Common";
import PosTerminalIcon from "./PosTerminalIcon";
import DSPosOnOffModal from "./DSPosOnOffModal";
import { postData } from "../../Hooks/apiCalls";
import { isDevelopment } from "../../Hooks/constants";

const DSPosOnOff = () => {
  const [locationData, setLocationData] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [posIds, setPosIds] = useState([]);
  const [filteredPosIds, setFilteredPosIds] = useState([]);
  const [selectedPosIds, setSelectedPosIds] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [action, setAction] = useState("Activate");
  const [deviceType, setDeviceType] = useState("pos");
  const [message, setMessage] = useState("");
  const [loader, setLoader] = useState(false);
  const [locationAndName, setLocationAndName] = useState("");

  // Derive unique cities
  const cities = [...new Set(locationData.map((item) => item.dc_city))].map(
    (name, index) => ({
      id: index + 1,
      name,
    }),
  );

  const filteredLocations = selectedCity
    ? locationData?.filter(
        (loc) =>
          loc.dc_city ===
            cities.find((city) => city.id === parseInt(selectedCity))?.name &&
          (loc.loc
            .toString()
            .toLowerCase()
            .includes(locationSearch.toLowerCase()) ||
            loc.loc_name.toLowerCase().includes(locationSearch.toLowerCase())),
      )
    : [];

  // Filter POS IDs based on action and device type
  useEffect(() => {
    const filtered = posIds
      .filter((pos) =>
        action === "Activate" ? pos.status === "N" : pos.status === "Y",
      )
      .filter((pos) =>
        deviceType === "mpos" ? pos.mpos === "Y" : pos.mpos === "N",
      );
    setFilteredPosIds(filtered);
    setSelectedPosIds([]); // Clear selected POS IDs when action or deviceType changes
  }, [action, deviceType, posIds]);

  const handlePosSelection = (posId) => {
    // Convert posId to string here
    const stringPosId = String(posId);
    setSelectedPosIds((prev) =>
      prev.includes(stringPosId)
        ? prev.filter((id) => id !== stringPosId)
        : [...prev, stringPosId],
    );
  };

  const handleSelectAll = () => {
    if (selectedPosIds.length === filteredPosIds.length) {
      setSelectedPosIds([]);
    } else {
      // Map and convert all POS IDs to strings
      setSelectedPosIds(filteredPosIds.map((pos) => String(pos.pos_id)));
    }
  };

  const handleSubmit = () => {
    if (selectedPosIds.length === 0) {
      toast.error("Please select at least one POS ID", { autoClose: 1500 });
      return;
    }
    setIsModalOpen(true);
  };

  const handleConfirmAction = async () => {
    setLoader(true);
    const payload = {
      pos_id: selectedPosIds, // This will now always be an array of strings
      loc: selectedLocation ?? "",
      event: action === "Activate" ? "activate" : "deactivate",
      rawPath: "/updatePosStatus",
      source: "ds",
    };
    try {
      const { data } = await postData(
        isDevelopment
          ? `${process.env.REACT_APP_DARSABER_DEV}`
          : `${process.env.REACT_APP_DARSABER_PROD}`,
        isDevelopment ? true : true,
        false,
        payload,
      );

      if (data?.status === "success") {
        toast.success(data?.message);
      } else {
        toast.error(data?.message);
      }
      setSelectedPosIds([]);
      setLocationSearch("");
      setSelectedLocation("");
      setPosIds([]);
      setFilteredPosIds([]);
      setIsModalOpen(false);
      setAction("Activate");
      setLoader(false);
      setMessage("");
      setLocationAndName("");
    } catch (error) {
      setMessage("Error performing action");
    }
    setLoader(false);
  };

  // API calls
  const getLocations = async () => {
    const payload = {
      rawPath: "/getLocData",
      source: "ds",
    };
    setLoader(true);
    try {
      const { data } = await postData(
        isDevelopment
          ? `${process.env.REACT_APP_DARSABER_DEV}`
          : `${process.env.REACT_APP_DARSABER_PROD}`,
        isDevelopment ? true : true,
        false,
        payload,
      );

      if (data?.status === "success") {
        setLocationData(data?.data);
        setLoader(false);
      } else {
        setLoader(false);
        toast.error("Failed to fetch the locations");
      }
    } catch (err) {
      setLoader(false);
      console.log(err);
    }
  };

  const getPosIds = async (location, name) => {
    const payload = { loc: location, rawPath: "/getPosData", source: "ds" };
    setLoader(true);
    try {
      const { data } = await postData(
        isDevelopment
          ? `${process.env.REACT_APP_DARSABER_DEV}`
          : `${process.env.REACT_APP_DARSABER_PROD}`,
        isDevelopment ? true : true,
        false,
        payload,
      );

      if (data?.status === "success") {
        setLoader(false);
        setPosIds(data?.data);
        setSelectedLocation(location);
        setLocationSearch("");
        setLocationAndName(`${location} - ${name} `);
      } else {
        setLoader(false);
        toast.error("Failed to fetch the POS IDs");
      }
    } catch (err) {
      setLoader(false);
      console.log(err);
    }
  };

  useEffect(() => {
    getLocations();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-gray-100 flex items-center justify-center p-6">
      <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-2xl transform transition-all duration-300 hover:shadow-2xl relative">
        <h1 className="text-3xl font-extrabold text-[#FA4B16] mb-8 text-center tracking-tight">
          Darksaber Manager
        </h1>

        {/* Action Radio Buttons */}
        <div className="mb-8 flex justify-center space-x-6">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="radio"
              name="action"
              value="Activate"
              checked={action === "Activate"}
              onChange={() => setAction("Activate")}
              className="w-5 h-5 accent-[#FA4B16] border-gray-300 rounded-full"
            />
            <span className="text-gray-700 font-medium">Activate</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="radio"
              name="action"
              value="Deactivate"
              checked={action === "Deactivate"}
              onChange={() => setAction("Deactivate")}
              className="w-5 h-5 accent-[#FA4B16] border-gray-300 rounded-full"
            />
            <span className="text-gray-700 font-medium">Deactivate</span>
          </label>
        </div>

        {/* City Dropdown */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Select City
          </label>
          <select
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FA4B16] transition-colors duration-200 bg-gray-50"
            value={selectedCity}
            onChange={(e) => {
              setSelectedCity(e.target.value);
              setSelectedLocation("");
              setLocationSearch("");
              setPosIds([]);
              setFilteredPosIds([]);
            }}>
            <option value="">Select a city</option>
            {cities.map((city) => (
              <option key={city.id} value={city.id}>
                {city.name}
              </option>
            ))}
          </select>
        </div>

        {/* Location Search and Dropdown */}
        <div className="mb-6 relative">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Search Location
          </label>
          <input
            type="text"
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FA4B16] transition-colors duration-200 bg-gray-50"
            placeholder="Search by location code or name..."
            value={locationSearch}
            onChange={(e) => setLocationSearch(e.target.value)}
          />
          {locationSearch && (
            <div className="absolute left-0 right-0 mt-1 max-h-40 overflow-y-auto border border-gray-200 rounded-lg bg-white shadow-lg z-10 animate-fadeIn">
              {filteredLocations.map((loc) => (
                <div
                  key={loc.loc}
                  className="p-3 hover:bg-indigo-50 cursor-pointer transition-colors duration-150"
                  onClick={() => {
                    getPosIds(loc.loc, loc.loc_name);
                  }}>
                  <span className="font-medium text-[#FA4B16]">{loc.loc}</span>{" "}
                  - {loc.loc_name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Location name  */}
        {locationAndName && (
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Location Name
            </label>
            <span className="text-[#FA4B16] font-bold">{locationAndName}</span>
          </div>
        )}

        {/* POS / MPOS Device Type */}
        {selectedLocation && (
          <div className="mb-6 animate-slideDown">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Select Device Type
            </label>
            <div className="mb-8 flex justify-left space-x-6">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="deviceType"
                  value="pos"
                  checked={deviceType === "pos"}
                  onChange={() => setDeviceType("pos")}
                  className="w-5 h-5 accent-[#FA4B16] border-gray-300 rounded-full"
                />
                <span className="text-gray-700 font-medium">POS</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="deviceType"
                  value="mpos"
                  checked={deviceType === "mpos"}
                  onChange={() => setDeviceType("mpos")}
                  className="w-5 h-5 accent-[#FA4B16] border-gray-300 rounded-full"
                />
                <span className="text-gray-700 font-medium">MPOS</span>
              </label>
            </div>
          </div>
        )}
        {/* POS IDs */}
        {selectedLocation && (
          <div className="mb-6 animate-slideDown">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              POS ID
            </label>
            <div className="h-52 border rounded-lg p-3 bg-gray-50">
              <div className="flex justify-start mb-2 pb-2 border-b border-[#fde1d8d5]">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={
                      filteredPosIds.length > 0 &&
                      selectedPosIds.length === filteredPosIds.length
                    }
                    onChange={handleSelectAll}
                    className="w-4 h-4 accent-[#FA4B16] border-gray-300 rounded"
                  />
                  <span className="text-gray-700 font-medium">Select All</span>
                </label>
              </div>
              <div className="h-36 overflow-y-scroll scrollbar-thin">
                {filteredPosIds.map((pos, index) => (
                  <div key={pos.pos_id} className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      // Convert pos_id to string to strictly check against state string values
                      checked={selectedPosIds.includes(String(pos.pos_id))}
                      onChange={() => handlePosSelection(pos.pos_id)}
                      className="w-4 h-4 border-gray-300 rounded accent-[#FA4B16]"
                    />
                    <div className="ml-2 text-gray-700 font-bold flex justify-between p-2 align-center items-center flex-1">
                      <div
                        className={
                          index % 2 === 0 ? "text-[#FA4B16]" : "text-gray-500"
                        }>
                        {pos.pos_id}
                      </div>
                      <span className="font-extrabold">-</span>
                      <div className="flex gap-2">
                        {pos.mpos === "Y" ? (
                          <PosTerminalIcon classname={"text-black"} />
                        ) : (
                          <PosTerminalIcon classname={"text-gray-50"} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        {selectedLocation && (
          <div className="flex justify-center">
            <button
              onClick={handleSubmit}
              className={`px-6 py-3 rounded-lg text-white font-semibold transition-colors duration-200 ${
                action === "Activate"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
              } disabled:opacity-50 flex items-center`}
              disabled={loader}>
              {loader && (
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {action}
            </button>
          </div>
        )}

        {/* Modal */}
      </div>
      <DSPosOnOffModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmAction}
        posIds={selectedPosIds}
        action={action}
      />
      <PlanoLoaderModal open={loader} text="Fetching details in progress" />
    </div>
  );
};

export default DSPosOnOff;
